import { Card, CardActionArea, CardHeader } from "@mui/material";
import { Link as RouterLink } from "react-router-dom";
import ItemsGrid from "../common/items/ItemsGrid";
import {
  Analytics,
  Approval,
  AutoMode,
  Handyman,
  MilitaryTech,
  Notes,
  People,
  PlayArrow,
  QuestionMark,
  Schedule,
  Settings,
  Storage,
  Task,
  Webhook,
} from "@mui/icons-material";
import { useAppSelector } from "../../app/hooks";
import { Role } from "../../app/backendTypes";

export enum AdminPage {
  Actions = "actions",
  Automations = "automations",
  Approvals = "approvals",
  Data = "data",
  Ext = "ext",
  Hooks = "hooks",
  Internal="internal",
  Logs = "logs",
  Metrics = "metrics",
  Roles = "roles",
  Schedule = "schedule",
  Tasks = "tasks",
  Tools = "tools",
  Users = "users",
}

function Admin() {
  const roles = useAppSelector((state) => state.auth.roles);

  const list = Object.values(AdminPage).filter((page) => {
    if (page === AdminPage.Data) {
      return roles.includes(Role.Developer);
    }

    if (page === AdminPage.Users) {
      return roles.includes(Role.Administrator) || roles.includes(Role.Security);
    }

    return roles.includes(Role.Administrator);
  });

  return <ItemsGrid list={list} render={(kind) => <AdminItem kind={kind} />} />;
}

export default Admin;

function AdminItem(props: { kind: string }) {
  const { kind } = props;
  const title = kind.charAt(0).toUpperCase() + kind.substring(1);
  const description = matchDescription(kind);
  const icon = matchIcon(kind);

  return (
    <Card>
      <CardActionArea component={RouterLink} to={kind}>
        <CardHeader title={title} subheader={description} avatar={icon} />
      </CardActionArea>
    </Card>
  );
}

function matchDescription(kind: string) {
  let description;

  switch (kind) {
    case AdminPage.Actions: {
      description = "Run custom actions";
      break;
    }

    case AdminPage.Automations: {
      description = "Manage automations";
      break;
    }

    case AdminPage.Approvals: {
      description = "Set approval steps for tasks";
      break;
    }

    case AdminPage.Data: {
      description = "Run commands in the data layer";
      break;
    }

    case AdminPage.Ext: {
      description = "Settings for extensions";
      break;
    }

    case AdminPage.Hooks: {
      description = "Manage webhooks";
      break;
    }

    case AdminPage.Logs: {
      description = "Read application logs";
      break;
    }

    case AdminPage.Metrics: {
      description = "View application metrics";
      break;
    }

    case AdminPage.Roles: {
      description = "View and edit user roles";
      break;
    }

    case AdminPage.Schedule: {
      description = "Manage scheduled tasks";
      break;
    }

    case AdminPage.Tasks: {
      description = "Overview of all tasks";
      break;
    }

    case AdminPage.Tools: {
      description = "Modify displayed information about tools";
      break;
    }

    case AdminPage.Users: {
      description = "View and manage users";
      break;
    }

    default: {
      description = "Unknown";
    }
  }

  return description;
}

function matchIcon(kind: string) {
  let icon;

  switch (kind) {
    case AdminPage.Actions: {
      icon = <PlayArrow />;
      break;
    }

    case AdminPage.Automations: {
      icon = <AutoMode />;
      break;
    }

    case AdminPage.Approvals: {
      icon = <Approval />;
      break;
    }

    case AdminPage.Data: {
      icon = <Storage />;
      break;
    }

    case AdminPage.Ext: {
      icon = <Settings />;
      break;
    }

    case AdminPage.Hooks: {
      icon = <Webhook />;
      break;
    }

    case AdminPage.Logs: {
      icon = <Notes />;
      break;
    }

    case AdminPage.Metrics: {
      icon = <Analytics />;
      break;
    }

    case AdminPage.Roles: {
      icon = <MilitaryTech />;
      break;
    }

    case AdminPage.Schedule: {
      icon = <Schedule />;
      break;
    }

    case AdminPage.Tasks: {
      icon = <Task />;
      break;
    }

    case AdminPage.Tools: {
      icon = <Handyman />;
      break;
    }

    case AdminPage.Users: {
      icon = <People />;
      break;
    }

    default: {
      icon = <QuestionMark />;
    }
  }

  return icon;
}
